import Phaser from "phaser";

/**
 * Unified input layer. Per PRD §9:
 *   - Primary: XInput gamepad. A = jump, X = attack, Y = interact/pause,
 *     Start = pause, D-pad / L-stick = move.
 *   - Fallback: keyboard for dev. Both WASD **and** arrow keys map to the
 *     same logical axes so UI cursors and the in-game backstep affordance
 *     work for either habit (left-hand WASD or right-hand arrows).
 *     Action keys: Space = jump, J = attack, K = interact, Esc = pause.
 *
 * Edge-triggered ("justDown") events use Phaser's built-in helpers so we
 * don't have to track previous-frame state per button.
 */
export interface InputState {
  moveX: number; // -1..1
  moveY: number; // -1..1
  jumpPressed: boolean;
  attackPressed: boolean;
  interactPressed: boolean;
  /** True iff either Esc or Start fired this frame. Kept for scenes
   *  that don't care which key (Shop, Author, Tutorial). */
  pausePressed: boolean;
  /** Esc key only (or B button on gamepad — Phaser button 1). RunnerScene
   *  uses this to mean "abort to launcher" when already paused, "open
   *  pause overlay" otherwise. */
  escPressed: boolean;
  /** Start button (gamepad index 9). RunnerScene uses this to mean
   *  "resume" when paused, "open pause overlay" otherwise — so Start
   *  always behaves like the P key. */
  startPressed: boolean;
}

export class InputService {
  private readonly scene: Phaser.Scene;
  private keys!: {
    left: Phaser.Input.Keyboard.Key[];
    right: Phaser.Input.Keyboard.Key[];
    up: Phaser.Input.Keyboard.Key[];
    down: Phaser.Input.Keyboard.Key[];
    jump: Phaser.Input.Keyboard.Key;
    attack: Phaser.Input.Keyboard.Key;
    /** Both K and Y trigger interact — K matches the original keyboard
     *  binding, Y mirrors the gamepad Y button so the on-screen "[Y]"
     *  prompts also work on keyboard. */
    interact: Phaser.Input.Keyboard.Key[];
    pause: Phaser.Input.Keyboard.Key;
  };

  // Track prior-frame state for gamepad edge detection (Phaser exposes
  // .justDown on keys but not directly on gamepad buttons in v3).
  private prevPadButtons: Record<string, boolean> = {};

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    if (!scene.input.keyboard) {
      throw new Error("InputService: Phaser keyboard plugin missing");
    }
    if (!scene.input.gamepad) {
      // Not fatal — keyboard fallback covers dev. Log so analyst sees it.
      console.warn("InputService: gamepad plugin missing; keyboard-only mode");
    }
    const KC = Phaser.Input.Keyboard.KeyCodes;
    const kb = scene.input.keyboard;
    this.keys = {
      left: [kb.addKey(KC.A), kb.addKey(KC.LEFT)],
      right: [kb.addKey(KC.D), kb.addKey(KC.RIGHT)],
      up: [kb.addKey(KC.W), kb.addKey(KC.UP)],
      down: [kb.addKey(KC.S), kb.addKey(KC.DOWN)],
      jump: kb.addKey(KC.SPACE),
      attack: kb.addKey(KC.J),
      interact: [kb.addKey(KC.K), kb.addKey(KC.Y)],
      pause: kb.addKey(KC.ESC),
    };
  }

  /** Edge-trigger helper for gamepad buttons. */
  private padJustDown(button: Phaser.Input.Gamepad.Button | undefined, key: string): boolean {
    const wasDown = this.prevPadButtons[key] === true;
    const isDown = button?.pressed === true;
    this.prevPadButtons[key] = isDown;
    return !wasDown && isDown;
  }

  private anyDown(keys: Phaser.Input.Keyboard.Key[]): boolean {
    const kb = this.scene.input.keyboard;
    if (!kb) return false;
    return keys.some((k) => kb.checkDown(k, 0));
  }

  /** Read all inputs for the current frame. Call once per update(). */
  read(): InputState {
    const pad = this.scene.input.gamepad?.pad1;
    let moveX = 0;
    let moveY = 0;
    let jump = false;
    let attack = false;
    let interact = false;
    let esc = false;
    let start = false;

    if (this.anyDown(this.keys.left)) moveX -= 1;
    if (this.anyDown(this.keys.right)) moveX += 1;
    if (this.anyDown(this.keys.up)) moveY -= 1;
    if (this.anyDown(this.keys.down)) moveY += 1;

    if (Phaser.Input.Keyboard.JustDown(this.keys.jump)) jump = true;
    if (Phaser.Input.Keyboard.JustDown(this.keys.attack)) attack = true;
    if (this.keys.interact.some((k) => Phaser.Input.Keyboard.JustDown(k))) interact = true;
    if (Phaser.Input.Keyboard.JustDown(this.keys.pause)) esc = true;

    // Gamepad. Phaser maps standard XInput buttons by index:
    //   0 = A, 1 = B, 2 = X, 3 = Y, 9 = Start.
    if (pad) {
      const stickX = pad.axes.length > 0 ? pad.axes[0]!.getValue() : 0;
      const stickY = pad.axes.length > 1 ? pad.axes[1]!.getValue() : 0;
      const dz = 0.2;
      if (Math.abs(stickX) > dz) moveX = stickX;
      if (Math.abs(stickY) > dz) moveY = stickY;
      if (moveX === 0 && pad.left) moveX = -1;
      if (moveX === 0 && pad.right) moveX = 1;
      if (moveY === 0 && pad.up) moveY = -1;
      if (moveY === 0 && pad.down) moveY = 1;

      if (this.padJustDown(pad.buttons[0], "A")) jump = true;
      if (this.padJustDown(pad.buttons[2], "X")) attack = true;
      if (this.padJustDown(pad.buttons[3], "Y")) interact = true;
      // Gamepad B button doubles as Esc — gives the controller player a
      // discrete "go back" / "abort from pause" key separate from Start.
      if (this.padJustDown(pad.buttons[1], "B")) esc = true;
      if (this.padJustDown(pad.buttons[9], "Start")) start = true;
    }

    return {
      moveX: Phaser.Math.Clamp(moveX, -1, 1),
      moveY: Phaser.Math.Clamp(moveY, -1, 1),
      jumpPressed: jump,
      attackPressed: attack,
      interactPressed: interact,
      pausePressed: esc || start,
      escPressed: esc,
      startPressed: start,
    };
  }
}
